import React, { useCallback, useEffect, useState } from 'react';
import { Card, Input, Select, Space, Tag, Button, Typography, Empty, Table, Tooltip } from 'antd';
import {
  SearchOutlined, ReloadOutlined, ClearOutlined, FacebookFilled, InstagramFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '../services/api';
import { Elenco, Voce } from '../components/Elenco';

const { Title, Text } = Typography;

interface Post {
  id: number;
  caption: string;
  media_type: string;
  status: string;
  facebook_post_id: string | null;
  instagram_post_id: string | null;
  created_at: string;
  user: { id: number; name: string; email: string; business_name: string } | null;
}

interface Paginator {
  data: Post[];
  current_page: number;
  last_page: number;
  total: number;
}

const STATO: Record<string, { t: string; c: string }> = {
  published:  { t: 'Pubblicato', c: 'success' },
  failed:     { t: 'Fallito',    c: 'error' },
  publishing: { t: 'In corso',   c: 'processing' },
  draft:      { t: 'Bozza',      c: 'default' },
};

export default function PostsPage() {
  const [result, setResult] = useState<Paginator | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const [ricerca, setRicerca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setRicerca(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page };
      if (ricerca) params.search = ricerca;
      if (status) params.status = status;
      const res = await adminApi.posts(params);
      setResult(res.data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [ricerca, status, page]);

  useEffect(() => { load(); }, [load]);

  const posts = result?.data ?? [];
  const filtriAttivi = Boolean(ricerca || status);
  const azzera = () => { setSearch(''); setRicerca(''); setStatus(undefined); setPage(1); };

  const social = (p: Post) => (
    <Space size={6}>
      {p.facebook_post_id && (
        <Tooltip title="Pubblicato su Facebook">
          <FacebookFilled style={{ color: '#1877F2', fontSize: 17 }} />
        </Tooltip>
      )}
      {p.instagram_post_id && (
        <Tooltip title="Pubblicato su Instagram">
          <InstagramFilled style={{ color: '#C13584', fontSize: 17 }} />
        </Tooltip>
      )}
      {!p.facebook_post_id && !p.instagram_post_id && <Text type="secondary">—</Text>}
    </Space>
  );

  const colonne: ColumnsType<Post> = [
    {
      title: 'Utente',
      dataIndex: ['user', 'name'],
      width: 200,
      render: (_, p) => p.user ? (
        <>
          <div style={{ fontWeight: 600, color: '#101527' }}>{p.user.name}</div>
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            {p.user.business_name || p.user.email}
          </Text>
        </>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Contenuto',
      dataIndex: 'caption',
      render: c => c
        ? <Text style={{ maxWidth: 380 }} ellipsis={{ tooltip: c }}>{c}</Text>
        : <Text type="secondary">senza testo</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'media_type',
      width: 96,
      responsive: ['lg'],
      render: t => <Tag>{t === 'video' ? 'Video' : 'Immagine'}</Tag>,
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      width: 130,
      render: s => {
        const x = STATO[s] || { t: s || '—', c: 'default' };
        return <Tag color={x.c}>{x.t}</Tag>;
      },
    },
    { title: 'Dove', width: 92, render: (_, p) => social(p) },
    {
      title: 'Data',
      dataIndex: 'created_at',
      width: 115,
      responsive: ['lg'],
      render: d => new Date(d).toLocaleDateString('it-IT'),
    },
  ];

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Post</Title>
          <Text type="secondary">
            {loading ? 'Caricamento…' : `${result?.total ?? 0} post in totale`}
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Aggiorna</Button>
      </div>

      <Card
        styles={{ body: { padding: 0 } }}
        title={
          <Space wrap size={10} style={{ padding: '4px 0' }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#98A1B8' }} />}
              placeholder="Cerca nel testo del post…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="filtro filtro-largo"
            />
            <Select
              allowClear placeholder="Tutti gli stati" value={status}
              onChange={v => { setStatus(v); setPage(1); }}
              className="filtro"
              options={[
                { value: 'published', label: 'Pubblicati' },
                { value: 'failed', label: 'Falliti' },
                { value: 'publishing', label: 'In corso' },
                { value: 'draft', label: 'Bozze' },
              ]}
            />
            {filtriAttivi && <Button icon={<ClearOutlined />} onClick={azzera}>Azzera</Button>}
          </Space>
        }
      >
        {/* La paginazione arriva dal server: la tabella non deve rifarla da sola */}
        <div className="solo-desktop">
          <Table<Post>
            rowKey="id"
            columns={colonne}
            dataSource={posts}
            loading={loading}
            pagination={{
              current: result?.current_page ?? 1,
              total: result?.total ?? 0,
              pageSize: 15,
              onChange: setPage,
              showSizeChanger: false,
              hideOnSinglePage: true,
              showTotal: (t, r) => `${r[0]}–${r[1]} di ${t}`,
              style: { padding: '0 20px' },
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={filtriAttivi ? 'Nessun post con questi filtri' : 'Ancora nessun post'}
                >
                  {filtriAttivi && <Button onClick={azzera}>Azzera i filtri</Button>}
                </Empty>
              ),
            }}
            scroll={{ x: 820 }}
          />
        </div>

        <div className="solo-mobile">
          <Elenco<Post>
            righe={posts}
            colonne={colonne}
            loading={loading}
            rowKey={p => p.id}
            pageSize={999}
            vuoto={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtriAttivi ? 'Nessun post con questi filtri' : 'Ancora nessun post'}
              />
            }
            scheda={p => (
              <>
                <div className="scheda-titolo">{p.user?.name || '—'}</div>
                <div className="scheda-sub">{p.caption?.slice(0, 90) || 'senza testo'}</div>
                <Voce etichetta="Stato">
                  <Tag color={(STATO[p.status] || { c: 'default' }).c}>
                    {(STATO[p.status] || { t: p.status }).t}
                  </Tag>
                </Voce>
                <Voce etichetta="Dove">{social(p)}</Voce>
                <Voce etichetta="Data">{new Date(p.created_at).toLocaleDateString('it-IT')}</Voce>
              </>
            )}
          />
          {result && result.last_page > 1 && (
            <Space style={{ padding: 14, width: '100%', justifyContent: 'center' }}>
              <Button disabled={result.current_page <= 1} onClick={() => setPage(p => p - 1)}>
                Precedente
              </Button>
              <Text type="secondary">{result.current_page} / {result.last_page}</Text>
              <Button disabled={result.current_page >= result.last_page} onClick={() => setPage(p => p + 1)}>
                Successiva
              </Button>
            </Space>
          )}
        </div>
      </Card>
    </>
  );
}
